import React, { useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { KeywordBubbleChartRef } from "./KeywordBubbleChart";
import { generateChartFilename } from "../utils/chartDownload";
import { exportChart } from "../utils/chartExport";
import DownloadChartModal, { DownloadChartOptions } from "./DownloadChartModal";

interface DownloadChartButtonProps {
  chartRef: React.RefObject<KeywordBubbleChartRef | null>;
  anchorKeyword: string;
  languageCode: string;
  locationCode: string | number;
  fetchDate?: string | null;
  disabled?: boolean;
  // Extra props for full export layout
  domainName: string;
  clusterSize: number;
  totalVolume: number;
  avgDifficulty: number;
  lastRefreshed: string;
  nextUpdate: string;
  // Whether the chart draws the cluster-center diamond, so the exported legend
  // lists it only when it's actually plotted (mirrors ColorLegends).
  showCenterMarker?: boolean;
}

const DownloadChartButton: React.FC<DownloadChartButtonProps> = ({
  chartRef,
  anchorKeyword,
  languageCode,
  locationCode,
  fetchDate,
  disabled = false,
  domainName,
  clusterSize,
  totalVolume,
  avgDifficulty,
  lastRefreshed,
  nextUpdate,
  showCenterMarker = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (options: DownloadChartOptions) => {
    if (!chartRef.current || isDownloading) return;

    const chartInstance = chartRef.current.getChartInstance();
    if (!chartInstance) {
      console.error("Chart instance not available");
      return;
    }

    setIsDownloading(true);
    try {
      const filename = generateChartFilename(
        anchorKeyword,
        languageCode,
        String(locationCode),
        fetchDate,
        options.format
      );

      await exportChart({
        chartInstance,
        includeLabels: options.includeLabels,
        format: options.format,
        domainName,
        anchorKeyword,
        stats: {
          keywords: clusterSize,
          totalVolume,
          avgDifficulty,
          lastRefreshed,
          nextUpdate,
        },
        filename,
        showCenterMarker,
      });

      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to download chart:", error);
      alert("Failed to download chart. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        disabled={disabled}
        className="flex items-center gap-1.5 py-[14px] px-[18px] bg-[#e7e2f8] disabled:bg-neutral-300 text-my-purple rounded-full font-semibold disabled:cursor-not-allowed cursor-pointer transition-all leading-[1]"
        title="Download chart"
      >
        <FileDownloadIcon style={{ fontSize: 20 }} />
        Download Chart
      </button>

      <DownloadChartModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
    </>
  );
};

export default DownloadChartButton;
